import { useId, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CONTACT_PROFILE_LEAD_SUGGESTIONS,
  CONTACT_PROFILE_LOCKED_MESSAGE,
  CONTACT_PROFILE_READ_ONLY_MESSAGE,
  formatDate,
  formatTime,
  type ContactProfileField,
  type ContactProfileGroupKey,
  type ContactProfileValues,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  SearchField,
  Select,
  Skeleton,
  Tabs,
  TextField,
  TextLink,
} from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import {
  contactKeys,
  updateContactProfile,
  useContactProfile,
  useContactProfileVersion,
  useContactProfileVersions,
  useInvalidateContact,
} from '../api';
import type { ContactProfileResponse, ContactProfileVersionItem } from '../types';
import {
  changeLine,
  draftOf,
  profileChanges,
  profileSections,
  type ProfileDraft,
  type ProfileSection,
} from './profile-view';
import styles from './ProfileDialog.module.css';

type ProfileTab = 'properties' | 'versions';

const HIDDEN_LINE = 'Finanzangaben sind für Ihre Rolle ausgeblendet.';
const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'ja' },
  { value: 'false', label: 'nein' },
];

export interface ProfileDialogProps {
  contactId: string;
  contactName: string;
  onClose: () => void;
}

/**
 * „Alle Eigenschaften\" (owner decision 12.10.2026): every property of the contact by group, searchable and
 * editable per group, and the immutable versions of the profile. Mounted only while open.
 */
export function ProfileDialog({ contactId, contactName, onClose }: ProfileDialogProps) {
  const mobile = useIsMobile();
  const panelId = useId();
  const [tab, setTab] = useState<ProfileTab>('properties');
  const profile = useContactProfile(contactId, true);

  return (
    <Dialog
      open
      onClose={onClose}
      width={880}
      title="Eigenschaften"
      subtitle={contactName}
      sheetOnMobile={false}
      overlayTop={mobile ? 0 : 60}
      className={styles.dialog}
      bodyClassName={styles.body}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <Tabs
        label="Eigenschaften"
        panelId={panelId}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'properties', label: 'Eigenschaften' },
          { value: 'versions', label: 'Versionen', count: profile.data?.versionCount },
        ]}
      />
      <div id={`${panelId}-${tab}`} role="tabpanel" className={styles.panel}>
        {profile.isError ? (
          <Banner tone="danger">{errorMessage(profile.error)}</Banner>
        ) : !profile.data ? (
          <ProfileSkeleton />
        ) : tab === 'properties' ? (
          <PropertiesPanel contactId={contactId} profile={profile.data} />
        ) : (
          <VersionsPanel
            contactId={contactId}
            hiddenGroups={profile.data.hiddenGroups}
            readOnlyGroups={profile.data.readOnlyGroups}
          />
        )}
      </div>
    </Dialog>
  );
}

function ProfileSkeleton() {
  return (
    <div className={styles.skeleton} aria-busy="true">
      <Skeleton width="40%" />
      <Skeleton variant="block" height={140} />
      <Skeleton variant="block" height={180} />
    </div>
  );
}

// --- Eigenschaften -------------------------------------------------------------------------------

function PropertiesPanel({ contactId, profile }: { contactId: string; profile: ContactProfileResponse }) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateContact();
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ContactProfileGroupKey | null>(null);
  const [draft, setDraft] = useState<ProfileDraft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sections = profileSections(profile.values, profile.hiddenGroups, {
    query,
    pinnedGroup: editing,
    readOnlyGroups: profile.readOnlyGroups,
  });

  function startEdit(group: ContactProfileGroupKey) {
    setEditing(group);
    setDraft(draftOf(group, profile.values));
    setErrors({});
    setFailure(null);
  }

  function cancel() {
    setEditing(null);
    setDraft({});
    setErrors({});
    setFailure(null);
  }

  async function save(group: ContactProfileGroupKey) {
    const body = profileChanges(group, draft, profile.values);
    if (Object.keys(body).length === 0) {
      cancel();
      return;
    }
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const next = await updateContactProfile(contactId, body);
      queryClient.setQueryData(contactKeys.profile(contactId), next);
      cancel();
      await invalidate(contactId);
    } catch (error) {
      const fields = error instanceof ApiError ? error.fields : {};
      const inline = Object.keys(fields).some((key) => key in draft);
      setErrors(fields);
      setFailure(inline ? null : errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <SearchField
        label="Eigenschaft suchen"
        placeholder="Eigenschaft suchen"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className={styles.search}
      />
      {sections.length === 0 ? (
        <p className={styles.muted}>Keine Eigenschaft gefunden.</p>
      ) : (
        sections.map((section) =>
          section.kind === 'hidden' ? (
            <p key="hidden" className={clsx(styles.muted, styles.hiddenLine)}>
              {HIDDEN_LINE}
            </p>
          ) : (
            <GroupBlock
              key={section.key}
              section={section}
              sources={profile.sources}
              actions={
                editing === section.key ? (
                  <>
                    <Button variant="ghost" size="small" onClick={cancel} disabled={saving}>
                      Abbrechen
                    </Button>
                    <Button size="small" loading={saving} onClick={() => void save(section.key)}>
                      Speichern
                    </Button>
                  </>
                ) : profile.canEdit && section.editable && !section.readOnly && editing === null ? (
                  <Button
                    variant="ghost"
                    size="small"
                    aria-label={`${section.label} bearbeiten`}
                    onClick={() => startEdit(section.key)}
                  >
                    Bearbeiten
                  </Button>
                ) : null
              }
              banner={editing === section.key && failure ? <Banner tone="danger">{failure}</Banner> : null}
              renderEdit={
                editing === section.key
                  ? (field) => (
                      <FieldInput
                        field={field}
                        text={draft[field.key] ?? ''}
                        error={errors[field.key]}
                        disabled={saving}
                        onChange={(text) => setDraft((current) => ({ ...current, [field.key]: text }))}
                      />
                    )
                  : null
              }
            />
          ),
        )
      )}
    </>
  );
}

interface GroupBlockProps {
  section: Extract<ProfileSection, { kind: 'group' }>;
  sources?: ContactProfileResponse['sources'] | undefined;
  actions?: ReactNode;
  banner?: ReactNode;
  renderEdit?: ((field: ContactProfileField) => ReactNode) | null | undefined;
}

function GroupBlock({ section, sources, actions, banner, renderEdit }: GroupBlockProps) {
  const headingId = useId();
  return (
    <section className={styles.group} aria-labelledby={headingId}>
      <div className={styles.groupHead}>
        <h3 id={headingId} className={styles.groupLabel}>
          {section.label}
        </h3>
        {actions ? <span className={styles.groupActions}>{actions}</span> : null}
      </div>
      {section.editable ? null : <p className={styles.muted}>{CONTACT_PROFILE_LOCKED_MESSAGE}</p>}
      {section.editable && section.readOnly ? (
        <p className={styles.muted}>{CONTACT_PROFILE_READ_ONLY_MESSAGE}</p>
      ) : null}
      {banner ? <div className={styles.banner}>{banner}</div> : null}
      <dl className={clsx(styles.rows, renderEdit && styles.rowsEditing)}>
        {section.rows.map((row) => {
          const source = sources?.[row.field.key];
          const editor = renderEdit && row.field.editable ? renderEdit(row.field) : null;
          return (
            <div key={row.field.key} className={styles.row}>
              <dt className={styles.label}>{row.field.label}</dt>
              <dd className={styles.value}>
                {editor ?? (
                  <>
                    <span className={clsx(row.display === '—' && styles.empty)}>{row.display}</span>
                    {source ? (
                      <span className={styles.source}>
                        {source.label} · {formatDate(source.at)}
                      </span>
                    ) : null}
                  </>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

interface FieldInputProps {
  field: ContactProfileField;
  text: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (text: string) => void;
}

function FieldInput({ field, text, error, disabled, onChange }: FieldInputProps) {
  const common = { label: field.label, labelHidden: true, size: 'compact' as const, error, disabled };
  switch (field.type) {
    case 'enum':
    case 'boolean':
      return (
        <Select
          {...common}
          placeholder="—"
          options={field.type === 'boolean' ? BOOLEAN_OPTIONS : (field.options ?? [])}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'cents':
      return (
        <TextField {...common} type="money" value={text} onChange={(event) => onChange(event.target.value)} />
      );
    case 'integer':
      return (
        <TextField
          {...common}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'percent':
      return (
        <TextField
          {...common}
          type="number"
          inputMode="decimal"
          min={0}
          max={60}
          step={1}
          trailing={<span className={styles.suffix}>%</span>}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'date':
      return (
        <TextField {...common} type="date" value={text} onChange={(event) => onChange(event.target.value)} />
      );
    case 'text':
      return <TextInput field={field} common={common} text={text} onChange={onChange} />;
  }
}

/** A text property; the lead properties offer their normalised values as suggestions (D-341). */
function TextInput({
  field,
  common,
  text,
  onChange,
}: {
  field: ContactProfileField;
  common: {
    label: string;
    labelHidden: boolean;
    size: 'compact';
    error: string | undefined;
    disabled: boolean;
  };
  text: string;
  onChange: (text: string) => void;
}) {
  const listId = useId();
  const suggestions = CONTACT_PROFILE_LEAD_SUGGESTIONS[field.key];
  return (
    <>
      <TextField
        {...common}
        type={field.key === 'email' ? 'email' : field.key === 'phone' ? 'tel' : 'text'}
        list={suggestions ? listId : undefined}
        value={text}
        onChange={(event) => onChange(event.target.value)}
      />
      {suggestions ? (
        <datalist id={listId}>
          {suggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

// --- Versionen -----------------------------------------------------------------------------------

function versionHeading(item: {
  version: number;
  sourceLabel: string;
  createdAt: string;
  authorName: string | null;
}) {
  const author = item.authorName ? ` · von ${item.authorName}` : '';
  return `V${item.version} · ${item.sourceLabel} · ${formatDate(item.createdAt)} ${formatTime(item.createdAt)}${author}`;
}

function VersionsPanel({
  contactId,
  hiddenGroups,
  readOnlyGroups,
}: {
  contactId: string;
  hiddenGroups: readonly ContactProfileGroupKey[];
  readOnlyGroups: readonly ContactProfileGroupKey[];
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const versions = useContactProfileVersions(contactId, true);

  if (selected !== null)
    return (
      <VersionDetail
        contactId={contactId}
        version={selected}
        hiddenGroups={hiddenGroups}
        readOnlyGroups={readOnlyGroups}
        onBack={() => setSelected(null)}
      />
    );
  if (versions.isError) return <Banner tone="danger">{errorMessage(versions.error)}</Banner>;
  if (!versions.data) return <ProfileSkeleton />;
  if (versions.data.versions.length === 0)
    return <EmptyState variant="centered" title="Noch keine Versionen." className={styles.emptyState} />;
  return (
    <ol className={styles.versions}>
      {versions.data.versions.map((item) => (
        <VersionRow key={item.version} item={item} onOpen={() => setSelected(item.version)} />
      ))}
    </ol>
  );
}

function VersionRow({ item, onOpen }: { item: ContactProfileVersionItem; onOpen: () => void }) {
  const lines = item.changes
    .map((change) => changeLine(change.key, change.before, change.after))
    .filter((line): line is string => line !== null);
  return (
    <li className={styles.version}>
      <div className={styles.versionHead}>
        <span className={styles.versionTitle}>{versionHeading(item)}</span>
        <Button variant="ghost" size="small" onClick={onOpen}>
          Stand ansehen
        </Button>
      </div>
      {lines.length > 0 ? (
        <ul className={styles.changes}>
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function VersionDetail({
  contactId,
  version,
  hiddenGroups,
  readOnlyGroups,
  onBack,
}: {
  contactId: string;
  version: number;
  hiddenGroups: readonly ContactProfileGroupKey[];
  readOnlyGroups: readonly ContactProfileGroupKey[];
  onBack: () => void;
}) {
  const detail = useContactProfileVersion(contactId, version);
  const values: ContactProfileValues = detail.data?.values ?? {};
  return (
    <>
      <TextLink as="button" onClick={onBack} className={styles.back}>
        Zurück zu den Versionen
      </TextLink>
      {detail.isError ? (
        <Banner tone="danger">{errorMessage(detail.error)}</Banner>
      ) : !detail.data ? (
        <ProfileSkeleton />
      ) : (
        <>
          <p className={styles.versionTitle}>{versionHeading(detail.data)}</p>
          {profileSections(values, hiddenGroups, { readOnlyGroups }).map((section) =>
            section.kind === 'hidden' ? (
              <p key="hidden" className={clsx(styles.muted, styles.hiddenLine)}>
                {HIDDEN_LINE}
              </p>
            ) : (
              <GroupBlock key={section.key} section={section} />
            ),
          )}
        </>
      )}
    </>
  );
}
